import { BuildClass, TW_OVERFLOW_ELLIPSIS, fsmData } from '../../../universal';
import { React, _ } from '../../lib';
import { List } from './list';
import { useStateSimple } from './meta-types';
import { stubListInstance } from './stubs';
import { TextSearched, searchText } from './text';
const TreeListComponent = (props, ref) => {
    const listRef = React.useRef(stubListInstance);
    const [value, setValue] = useStateSimple(props.value, props.onUpdate);
    const [expanded, setExpanded] = useStateSimple(props.expanded, props.onUpdateExpanded);
    const toggleExpansion = React.useCallback((v) => {
        if (v == null) {
            return;
        }
        else if (expanded.includes(v)) {
            setExpanded(expanded.filter(x => x != v));
        }
        else {
            setExpanded(expanded.concat(v));
        }
    }, [setExpanded, expanded]);
    const options = React.useMemo(() => getFlatOptions(props.items, expanded, props.searchText ?? null), [props.items, expanded, props.searchText]);
    const navigateLeft = React.useCallback(() => {
        const selected = _.first(options.filter(x => x.value == value));
        if (selected == null) {
            return;
        }
        if (selected.isGroup && selected.isExpanded) {
            toggleExpansion(value);
        }
        else {
            setValue(selected.parent ?? value);
        }
    }, [options, toggleExpansion, setValue, value]);
    const navigateRight = React.useCallback(() => {
        const selected = _.first(options.filter(x => x.value == value));
        if (selected == null || !selected.isGroup) {
            return;
        }
        if (!selected.isExpanded) {
            toggleExpansion(value);
        }
        else {
            setValue(selected.items[0] ?? value);
        }
    }, [options, toggleExpansion, setValue, value]);
    const onKeyDown = React.useCallback((ev, defaultEvent) => {
        defaultEvent(ev);
        switch (ev.which) {
            case 37:
                navigateLeft();
                break;
            case 39:
                navigateRight();
                break;
            default:
                return;
        }
        ev.preventDefault();
        ev.stopPropagation();
    }, [navigateLeft, navigateRight]);
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            listRef.current?.focus();
        },
        select: () => {
            listRef.current?.focus();
        },
        getElement: () => listRef.current?.getElement(),
        getList: () => listRef.current,
    }));
    return (React.createElement(List, { className: BuildClass({
            'ui5-tree-list h-full': true,
            [props.className ?? '']: true,
        }), style: props.style, ref: listRef, value: value, onUpdate: setValue, height: 24, multiple: false, onKeyDown: onKeyDown, rows: options.map(x => ({
            value: x.value,
            selectable: props.selectableGroups || !x.isGroup,
            className: 'leading-6 hover:bg-yellow-100',
            content: () => (React.createElement("label", { className: BuildClass({
                    block: true,
                    'font-bold': x.value == value,
                    'cursor-pointer': x.value != value,
                    [TW_OVERFLOW_ELLIPSIS]: true,
                }), style: { paddingLeft: `${x.indent * 16}px` }, title: x.label, onClick: () => {
                    if (x.isGroup) {
                        toggleExpansion(x.value);
                    }
                } },
                React.createElement("span", { className: "inline-block w-4 align-top px-[5px]" }, x.isGroup && (React.createElement("img", { className: BuildClass({
                        'inline w-[6px] align-middle': true,
                        '-rotate-90': x.isGroup && !x.isExpanded,
                    }), src: "/static/img/svg/triangle-down.svg" }))),
                React.createElement("span", { className: "w-[calc(100%-16px)] align-top" },
                    React.createElement(TextSearched, { needles: props.searchText ?? null, text: x.label, className: "bg-[#ff0] font-bold text-black" })))),
        })) }));
};
const getFlatOptions = (items, expanded, search) => {
    const options = [];
    const isSearching = search != null && search.length > 0;
    const addItems = (parents, item, indent) => {
        const internalItem = {
            value: item.value,
            label: item.label,
            indent: indent,
            parent: _.last(parents),
            parents: parents,
            items: item.items?.map(x => x.value) ?? [],
            isGroup: item.items != null,
            isExpanded: expanded.includes(item.value) || isSearching,
            passedSearchFilter: searchText(item.label, search),
        };
        options.push(internalItem);
        if (internalItem.isGroup && internalItem.isExpanded) {
            _.forEach(item.items, x => {
                addItems(parents.concat(item.value), x, indent + 1);
            });
        }
        return internalItem;
    };
    _.forEach(items, x => {
        addItems([], x, 0);
    });
    const items_passed = new Set(_.uniq(_.flatten(fsmData(options, {
        filter: x => x.passedSearchFilter,
        map: x => x.parents.concat(x.value),
    }))));
    return options.filter(x => items_passed.has(x.value));
};
export const TreeList = React.forwardRef(TreeListComponent);
//# sourceMappingURL=tree-list.js.map